import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Code, 
  Database, 
  Server, 
  Globe, 
  Shield, 
  Users, 
  BarChart3, 
  BookOpen,
  Play,
  Clock,
  Star,
  TrendingUp,
  ChevronRight,
  Zap,
  Target,
  Award,
  Smartphone,
  Building,
  Palette,
  Lightbulb,
  FileText,
  Wrench,
  ArrowRight
} from 'lucide-react';
import { 
  backendData, 
  frontendData, 
  mobileData, 
  qaData, 
  pmData, 
  ceoData 
} from '../mocks';

const Dashboard = ({ user }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const navigate = useNavigate();

  // Función para obtener el ícono por nombre
  const getIconByName = (iconName) => {
    const iconMap = {
      'Code': Code,
      'Server': Server,
      'Globe': Globe,
      'Shield': Shield,
      'Users': Users,
      'BarChart3': BarChart3,
      'BookOpen': BookOpen,
      'Play': Play,
      'Target': Target,
      'Award': Award,
      'Smartphone': Smartphone,
      'Building': Building,
      'Palette': Palette,
      'Lightbulb': Lightbulb,
      'FileText': FileText,
      'Database': Database,
      'Zap': Zap,
      'Wrench': Wrench
    };
    return iconMap[iconName] || Code;
  };

  // Obtener datos de los mocks
  const roles = [
    backendData,
    frontendData,
    mobileData,
    qaData,
    pmData,
    ceoData
  ].map(role => ({
    ...role,
    icon: getIconByName(role.icon)
  }));

  // Función para manejar clic en recurso
  const handleResourceClick = (resource) => {
    navigate(`/resources/${resource.id}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto">
        {/* Header del Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold gradient-text mb-2">
            ¡Bienvenido, {user.name}! 👋
          </h1>
          <p className="text-text-secondary text-lg">
            Accede libremente a todos los recursos y herramientas organizados por tu rol en Innova Developers
          </p>
        </motion.div>

        {/* Botón para ir a recursos */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Link
            to="/resources"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-primary to-accent text-white rounded-lg hover:from-primary/90 hover:to-accent/90 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <BookOpen className="w-5 h-5" />
            <span className="font-semibold">Explorar Todos los Recursos</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>

        {/* Estadísticas rápidas */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          {[
            { label: 'Recursos Disponibles', value: '45+', icon: Award, color: 'from-primary to-accent' },
            { label: 'Herramientas Activas', value: '12', icon: Clock, color: 'from-secondary to-purple-600' },
            { label: 'Documentos Útiles', value: '28', icon: Star, color: 'from-yellow-500 to-orange-600' },
            { label: 'Acceso Libre', value: '100%', icon: TrendingUp, color: 'from-green-500 to-emerald-600' }
          ].map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="card hover-lift"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-text mb-1">{stat.value}</h3>
              <p className="text-text-secondary text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Roles principales */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          <h2 className="text-2xl font-bold text-text mb-6">
            Recursos disponibles para:
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roles.map((role) => (
              <motion.div
                key={role.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="card cursor-pointer hover-lift"
                onClick={() => setSelectedRole(selectedRole === role.id ? null : role.id)}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${role.color} rounded-xl flex items-center justify-center mb-4`}>
                  <role.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-text mb-2">{role.title}</h3>
                <p className="text-text-secondary text-sm mb-4">{role.description}</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">
                    {role.categories.length} recursos
                  </span>
                  <ChevronRight 
                    className={`w-5 h-5 text-text-secondary transition-transform ${
                      selectedRole === role.id ? 'rotate-90' : ''
                    }`} 
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Categorías expandidas */}
        <AnimatePresence>
          {selectedRole && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-8"
            >
              <div className="card">
                <h3 className="text-2xl font-bold text-text mb-6">
                  {roles.find(r => r.id === selectedRole)?.title} - Recursos Disponibles
                </h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {roles.find(r => r.id === selectedRole)?.categories.map((category, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border border-gray-700 rounded-lg p-6 hover:border-primary transition-colors"
                    >
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                          {(() => {
                            const IconComponent = getIconByName(category.icon);
                            return <IconComponent className="w-5 h-5 text-white" />;
                          })()}
                        </div>
                        <h4 className="text-lg font-semibold text-text">{category.name}</h4>
                      </div>
                      
                      <div className="space-y-3">
                        {category.resources.map((resource, resourceIndex) => (
                          <div 
                            key={resourceIndex} 
                            className="flex items-center justify-between p-3 bg-surface rounded-lg cursor-pointer hover:bg-gray-800 transition-colors"
                            onClick={() => handleResourceClick(resource)}
                          >
                            <div className="flex-1">
                              <h5 className="font-medium text-text">{resource.title}</h5>
                              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                                <span className="flex items-center space-x-1">
                                  <Clock className="w-4 h-4" />
                                  <span>{resource.estimatedTime}</span>
                                </span>
                                <span className="px-2 py-1 bg-gray-800 rounded-full text-xs">
                                  {resource.difficulty}
                                </span>
                                <span className="px-2 py-1 bg-primary/20 text-primary rounded-full text-xs">
                                  {resource.type}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-3">
                              <div className="text-right">
                                <div className="flex items-center space-x-1 text-sm text-text-secondary">
                                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                  <span>{resource.rating}</span>
                                </div>
                                <div className="text-xs text-text-secondary">
                                  {resource.downloads} descargas
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Dashboard;
